import { supabase } from '@/integrations/supabase/client';

// Safe storage access — in-app browsers (e.g., X/Twitter on iOS) may block
// sessionStorage access and throw SecurityError in restricted WKWebView contexts.
function safeGetSession(key: string): string | null {
  try { return sessionStorage.getItem(key); } catch (err) { console.error('[safeGetSession]', err); return null; }
}
function safeSetSession(key: string, value: string): void {
  try { sessionStorage.setItem(key, value); } catch (err) { console.error('[safeSetSession]', err); }
}

// Generate or retrieve session ID
function getSessionId(): string {
  let sessionId = safeGetSession('obha_session_id');
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    safeSetSession('obha_session_id', sessionId);
  }
  return sessionId;
}

// Geo location — fetched once per session, cached in sessionStorage
interface GeoInfo { country: string; city: string; }
let geoCache: GeoInfo | null = null;
let geoPromise: Promise<GeoInfo> | null = null;

async function getGeo(): Promise<GeoInfo> {
  if (geoCache) return geoCache;
  const cached = safeGetSession('obha_geo');
  if (cached) {
    try { geoCache = JSON.parse(cached); return geoCache!; } catch (err) { console.error('[getGeo]', 'Failed to parse cached geo', err); }
  }
  if (geoPromise) return geoPromise;
  geoPromise = (async () => {
    // Try ipapi.co, fall back to ipwho.is (ipapi.co blocks CORS from browsers often)
    const endpoints: Array<{ url: string; parse: (d: any) => GeoInfo }> = [
      { url: 'https://ipwho.is/', parse: (d) => ({ country: d.country || '', city: d.city || '' }) },
      { url: 'https://ipapi.co/json/', parse: (d) => ({ country: d.country_name || '', city: d.city || '' }) },
    ];
    for (const ep of endpoints) {
      try {
        const res = await fetch(ep.url, { signal: AbortSignal.timeout(3000) });
        if (res.ok) {
          const geo = ep.parse(await res.json());
          if (geo.country) {
            safeSetSession('obha_geo', JSON.stringify(geo));
            geoCache = geo;
            return geo;
          }
        }
      } catch { /* try next */ }
    }
    return { country: '', city: '' };
  })();
  return geoPromise;
}

// Build tracking body with geo
async function buildBody(base: Record<string, any>): Promise<Record<string, any>> {
  const geo = await getGeo();
  return { ...base, country: geo.country || undefined, city: geo.city || undefined };
}

// Track page view
export async function trackPageView(path: string, title?: string) {
  try {
    const body = await buildBody({
      sessionId: getSessionId(),
      eventType: 'page_view',
      pageUrl: window.location.href,
      pageTitle: title || document.title,
      referrer: document.referrer,
      eventData: { path }
    });
    await supabase.functions.invoke('track-event', { body });
  } catch (error) {
    console.debug('Tracking error:', error);
  }
}

// Track click event
export async function trackClick(
  elementType: string,
  elementId?: string,
  elementText?: string,
  elementClass?: string
) {
  try {
    const body = await buildBody({
      sessionId: getSessionId(),
      eventType: 'click',
      pageUrl: window.location.href,
      eventData: {
        elementType,
        elementId,
        elementText: elementText?.substring(0, 100),
        elementClass
      }
    });
    await supabase.functions.invoke('track-event', { body });
  } catch (error) {
    console.debug('Tracking error:', error);
  }
}

// Track custom event
export async function trackEvent(
  eventType: string,
  eventData?: Record<string, any>
) {
  try {
    const body = await buildBody({
      sessionId: getSessionId(),
      eventType,
      pageUrl: window.location.href,
      pageTitle: document.title,
      eventData
    });
    await supabase.functions.invoke('track-event', { body });
  } catch (error) {
    console.debug('Tracking error:', error);
  }
}

// Track cart action
export async function trackCartAction(action: 'add' | 'remove' | 'update', productId: string, quantity: number) {
  await trackEvent('cart_action', { action, productId, quantity });
}

// Track checkout step
export async function trackCheckoutStep(step: string, data?: Record<string, any>) {
  await trackEvent('checkout_step', { step, ...data });
}

// Track search
export async function trackSearch(query: string, results?: number) {
  await trackEvent('search', { query, results });
}

// Track error
export async function trackError(errorType: string, errorMessage: string, errorData?: Record<string, any>) {
  await trackEvent('error', { errorType, errorMessage, ...errorData });
}

// Track performance metrics
export async function trackPerformance() {
  try {
    const perfData = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    if (perfData) {
      await trackEvent('performance', {
        loadTime: Math.round(perfData.loadEventEnd - perfData.fetchStart),
        domContentLoaded: Math.round(perfData.domContentLoadedEventEnd - perfData.fetchStart),
        firstPaint: Math.round(perfData.responseStart - perfData.fetchStart)
      });
    }
  } catch (error) {
    console.debug('Performance tracking error:', error);
  }
}

// Track begin checkout
export async function trackBeginCheckout(cartValue: number, itemCount: number) {
  await trackEvent('begin_checkout', { cartValue, itemCount });
}

interface TrackingItem {
  id?: string;
  product_id?: string;
  name?: string;
  price?: number;
  quantity?: number;
  [key: string]: unknown;
}

// Track cart abandonment
export async function trackCartAbandonment(cart: {
  items: TrackingItem[];
  total: number;
  email?: string;
  name?: string;
  phone?: string;
}) {
  try {
    const body = await buildBody({
      sessionId: getSessionId(),
      eventType: 'cart_abandoned',
      pageUrl: window.location.href,
      eventData: {
        item_count: cart.items.length,
        total: cart.total,
        email: cart.email,
        name: cart.name,
        phone: cart.phone,
        items: cart.items,
      }
    });
    await supabase.functions.invoke('track-event', { body });
  } catch (error) {
    console.debug('Cart abandonment tracking error:', error);
  }
}

// Mark cart as recovered
export async function markCartRecovered(orderId: string) {
  try {
    const body = await buildBody({
      sessionId: getSessionId(),
      eventType: 'cart_recovered',
      pageUrl: window.location.href,
      eventData: { orderId }
    });
    await supabase.functions.invoke('track-event', { body });
  } catch (error) {
    console.debug('Cart recovery tracking error:', error);
  }
}

// Auto-track clicks on important elements
export function setupAutoTracking() {
  // Track all button clicks
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    
    // Track button clicks
    if (target.tagName === 'BUTTON' || target.closest('button')) {
      const button = target.tagName === 'BUTTON' ? target : target.closest('button');
      trackClick('button', button.id, button.textContent || undefined, button.className);
    }
    
    // Track link clicks
    if (target.tagName === 'A' || target.closest('a')) {
      const link = target.tagName === 'A' ? target : target.closest('a') as HTMLAnchorElement;
      trackClick('link', link.id, link.textContent || undefined, link.href);
    }
    
    // Track product card clicks
    if (target.closest('[data-product-id]')) {
      const card = target.closest('[data-product-id]');
      trackClick('product_card', card.dataset.productId, undefined, card.className);
    }
  }, true); // Use capture phase to catch all clicks

  // Track performance after page load
  if (document.readyState === 'complete') {
    setTimeout(trackPerformance, 1000);
  } else {
    window.addEventListener('load', () => {
      setTimeout(trackPerformance, 1000);
    });
  }
}
